import { X, Volume2, VolumeX, RotateCcw, LayoutGrid, Trophy } from 'lucide-react';

export default function FinalPodium({ participants, onPlayAgain, onBackToDashboard, isMuted, onToggleMute }) {
  const sortedParticipants = [...participants].sort((a, b) => (b.score || 0) - (a.score || 0));
  const top3 = sortedParticipants.slice(0, 3);
  const rest = sortedParticipants.slice(3, 9);

  const getInitial = (name) => name?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="font-body h-dvh overflow-hidden bg-mp-bg-blue text-mp-darkgreen">
      <div className="relative flex h-full w-full flex-col overflow-hidden">
        {/* Confetti decorations */}
        <div className="absolute inset-0 pointer-events-none opacity-70">
          <span className="absolute top-[8%] left-[10%] w-3 h-3 bg-mp-magenta rounded-sm rotate-[20deg]" />
          <span className="absolute top-[14%] right-[15%] w-3 h-3 bg-mp-teal rounded-sm -rotate-[12deg]" />
          <span className="absolute top-[20%] left-[42%] w-2 h-2 bg-mp-fox-rust rounded-full" />
          <span className="absolute top-[28%] left-[8%] w-3 h-3 bg-mp-lime rounded-sm" />
          <span className="absolute bottom-[20%] right-[8%] w-3 h-3 bg-mp-magenta rounded-sm rotate-[15deg]" />
          <span className="absolute bottom-[28%] left-[14%] w-2 h-2 bg-mp-darkgreen rounded-full" />
        </div>

        {/* Top Navigation */}
        <header className="relative z-10 flex items-center justify-between p-4 pt-6 max-w-5xl mx-auto w-full">
          <button onClick={onBackToDashboard} className="p-2 rounded-full hover:bg-mp-darkgreen/5 text-mp-darkgreen/70 transition-colors" aria-label="Lukk">
            <X size={22} />
          </button>
          <span className="mp-eyebrow">Mikkel kviss · Slutt</span>
          <button onClick={onToggleMute} className="p-2 rounded-full hover:bg-mp-darkgreen/5 text-mp-darkgreen/70 transition-colors" aria-label="Lyd av/på">
            {isMuted ? <VolumeX size={22} /> : <Volume2 size={22} />}
          </button>
        </header>

        {/* Headline */}
        <div className="relative z-10 flex flex-col items-center text-center mt-2 mb-4 px-4">
          <h1 className="font-display text-mp-darkgreen text-4xl md:text-5xl tracking-tight leading-tight">
            Det blei eit minne!
          </h1>
          <p className="font-body text-mp-stone-700 mt-2 max-w-md">
            Takk for at de var med. Mikkel og venene gratulerer vinnarane.
          </p>
        </div>

        {/* Podium Section */}
        <div className="relative z-10 w-full max-w-3xl mx-auto flex items-end justify-center gap-3 px-4 mb-4">
          {/* 2nd */}
          {top3[1] && (
            <div className="flex flex-col items-center justify-end w-1/3">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white shadow-mp-2 border-4 border-mp-teal flex items-center justify-center mb-3">
                <span className="font-display text-mp-teal text-2xl md:text-3xl">{getInitial(top3[1].name)}</span>
              </div>
              <p className="font-display text-mp-darkgreen text-base md:text-lg leading-tight truncate w-28 text-center mb-1">{top3[1].name}</p>
              <p className="font-display text-mp-magenta text-base mb-2">{(top3[1].score || 0).toLocaleString()} p</p>
              <div className="w-full bg-mp-teal text-white rounded-t-2xl h-32 md:h-44 flex flex-col items-center justify-start pt-4 px-2 shadow-mp-2">
                <span className="font-display text-5xl md:text-6xl leading-none -tracking-[0.04em]">2</span>
                <span className="font-display text-[11px] uppercase tracking-[0.18em] opacity-70 mt-1">Andreplass</span>
              </div>
            </div>
          )}

          {/* 1st */}
          {top3[0] && (
            <div className="flex flex-col items-center justify-end w-1/3 z-20 -mb-1">
              <Trophy size={36} className="text-mp-fox-rust mb-2 animate-bounce" />
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-white shadow-mp-3 border-4 border-mp-lime flex items-center justify-center mb-3">
                <span className="font-display text-mp-darkgreen text-4xl md:text-5xl">{getInitial(top3[0].name)}</span>
              </div>
              <p className="font-display text-mp-darkgreen text-lg md:text-xl leading-tight truncate w-32 text-center mb-1">{top3[0].name}</p>
              <p className="font-display text-mp-magenta text-lg mb-2">{(top3[0].score || 0).toLocaleString()} p</p>
              <div className="w-full bg-mp-lime text-mp-darkgreen rounded-t-2xl h-44 md:h-56 flex flex-col items-center justify-start pt-5 px-2 shadow-mp-3">
                <span className="font-display text-6xl md:text-7xl leading-none -tracking-[0.04em]">1</span>
                <span className="font-display text-[12px] uppercase tracking-[0.18em] opacity-70 mt-1">Vinnar</span>
              </div>
            </div>
          )}

          {/* 3rd */}
          {top3[2] && (
            <div className="flex flex-col items-center justify-end w-1/3">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white shadow-mp-2 border-4 border-mp-bg-mint flex items-center justify-center mb-3">
                <span className="font-display text-mp-darkgreen text-2xl md:text-3xl">{getInitial(top3[2].name)}</span>
              </div>
              <p className="font-display text-mp-darkgreen text-base md:text-lg leading-tight truncate w-28 text-center mb-1">{top3[2].name}</p>
              <p className="font-display text-mp-magenta text-base mb-2">{(top3[2].score || 0).toLocaleString()} p</p>
              <div className="w-full bg-mp-bg-mint text-mp-darkgreen rounded-t-2xl h-24 md:h-32 flex flex-col items-center justify-start pt-3 px-2 shadow-mp-2">
                <span className="font-display text-4xl md:text-5xl leading-none -tracking-[0.04em]">3</span>
                <span className="font-display text-[11px] uppercase tracking-[0.18em] opacity-70 mt-1">Tredjeplass</span>
              </div>
            </div>
          )}
        </div>

        {/* Resten av flokken */}
        {rest.length > 0 && (
          <div className="relative z-10 w-full max-w-md mx-auto px-4 mb-4">
            <div className="bg-white rounded-2xl border border-[rgba(17,73,66,0.10)] p-4 shadow-mp-1">
              <p className="font-display text-[11px] uppercase tracking-[0.18em] text-mp-teal mb-2">Resten av flokken</p>
              <div className="flex flex-col">
                {rest.map((p, idx) => (
                  <div key={p.id} className={`grid grid-cols-[28px_1fr_auto] items-center gap-3 py-1.5 px-2 rounded ${idx % 2 === 0 ? 'bg-mp-bg-lightgreen' : ''}`}>
                    <span className="font-display text-mp-stone-500 text-sm">{idx + 4}</span>
                    <span className="font-display text-mp-darkgreen text-base truncate">{p.name}</span>
                    <span className="font-display text-mp-magenta text-base tabular-nums">{(p.score || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Actions */}
        <div className="relative z-30 w-full pb-8 pt-2">
          <div className="flex flex-col gap-3 px-6 max-w-md mx-auto">
            <button onClick={onPlayAgain} className="mp-cta mp-cta-large w-full">
              <RotateCcw size={20} />
              <span>Spel igjen</span>
            </button>
            <button
              onClick={onBackToDashboard}
              className="flex w-full items-center justify-center gap-2 rounded-full h-12 bg-white border border-[rgba(17,73,66,0.20)] text-mp-darkgreen font-display font-bold text-sm hover:bg-mp-bg-lightgreen transition-colors"
            >
              <LayoutGrid size={18} />
              <span>Tilbake til oversikta</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
